import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Send, Loader2, Phone } from "lucide-react";
import { Button } from "../../components/ui/button";

export default function Inbox({ canWrite }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const loadThreads = useCallback(() => { api.get("/sms/threads").then((r) => setThreads(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 20000); return () => clearInterval(t); }, [loadThreads]);

  const openThread = (key) => {
    setActive(key);
    api.get(`/sms/threads/${key}`).then((r) => { setMsgs(r.data); loadThreads(); }).catch(() => {});
  };
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => { api.get(`/sms/threads/${active}`).then((r) => setMsgs(r.data)).catch(() => {}); }, 15000);
    return () => clearInterval(t);
  }, [active]);

  const relTime = (iso) => {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "now";
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/sms/threads/${active}/reply`, { body: reply.trim() });
      setReply(""); openThread(active);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const activeThread = threads.find((t) => t.contact_key === active);
  const fmtT = (iso) => { const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sms-inbox">
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border text-sm font-semibold flex items-center gap-2"><Phone className="w-4 h-4 text-primary" /> Conversations</div>
        {threads.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">No SMS conversations yet. Inbound tenant texts will appear here.</p> : (
          <div className="max-h-[60vh] overflow-y-auto">
            {threads.map((t) => (
              <button key={t.contact_key} onClick={() => openThread(t.contact_key)} data-testid={`thread-${t.contact_key}`}
                className={`w-full text-left px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${active === t.contact_key ? "bg-muted/60" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{t.tenant_name || t.phone}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-muted-foreground">{relTime(t.last_at)}</span>
                    {t.unread > 0 && <span className="text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5" data-testid={`unread-${t.contact_key}`}>{t.unread}</span>}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last_direction === "out" ? "You: " : ""}{t.last_body}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="md:col-span-2 rounded-2xl border border-border bg-card flex flex-col min-h-[50vh]">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Select a conversation to view messages.</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-semibold">{activeThread?.tenant_name || activeThread?.phone}</p>
              <p className="text-xs text-muted-foreground">{activeThread?.phone}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[50vh]" data-testid="thread-messages">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.direction === "out" ? (m.status === "Failed" ? "bg-destructive text-destructive-foreground rounded-br-sm" : "bg-primary text-primary-foreground rounded-br-sm") : "bg-muted rounded-bl-sm"}`}>
                    <p className="whitespace-pre-line">{m.body}</p>
                    <p className={`text-[10px] mt-1 ${m.direction === "out" ? (m.status === "Failed" ? "text-destructive-foreground/80" : "text-primary-foreground/70") : "text-muted-foreground"}`}>{fmtT(m.created_at)}{m.direction === "out" && m.status === "Failed" ? " · Not delivered" : ""}</p>
                  </div>
                </div>
              ))}
            </div>
            {canWrite && (
              <div className="border-t border-border p-3 flex items-end gap-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} data-testid="reply-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type an SMS reply…" className="flex-1 rounded-lg border border-border bg-background p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
                <Button className="rounded-xl" onClick={send} disabled={sending || !reply.trim()} data-testid="reply-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
